

































import { computed, defineComponent, onBeforeUnmount, onMounted, PropType, ref, watch } from "@vue/composition-api";
import { HomeFeedItem } from "@scrile/api-provider/dist/projects/scrileConnect/HomeFeedProvider";
import useCoverThumbnail from "@/hooks/useCoverThumbnail";
import { getFeedCoParticipants, getFeedThumbnail, getFeedTrailer } from "@/lib/contentFeedHelper";
import { getElement } from "@/lib/elementHelpers";
import { $t } from "@/lib/i18nHelpers";
import { MediaFilesController } from "@/lib/MediaFilesController";
import { timeAgo } from "@/lib/time";
import { getProfileUrl } from "@/lib/urlHelpers";
import { getDistance } from "@/lib/userHelpers";
import AvatarsGroup from "@/components/AvatarsGroup";
import BaseIcon from "@/components/Base/BaseIcon";
import BaseAvatar from "@/components/BaseAvatar";
import { HOME_FEED_CARD_WIDTH, TRAILER_START_DELAY } from "../../const";

export default defineComponent({
  name: "HomeFeedCard",
  components: { AvatarsGroup, BaseAvatar, BaseIcon },
  props: {
    item: { type: Object as PropType<HomeFeedItem>, required: true },
    showNotSafeForWork: { type: Boolean, default: false },
    showDistance: { type: Boolean, default: false },
    isFollowed: { type: Boolean, default: false },
    playTrailer: { type: Boolean, default: false },
    playTrailerOnHover: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const card = ref();
    const hovered = ref(false);
    const playing = ref(false);

    const files = computed(() => props.item.contentFeed?.files ?? []);
    const thumbnail = computed(() => getFeedThumbnail(files.value, props.showNotSafeForWork));
    const { coverSrc: thumbnailSrc } = useCoverThumbnail(thumbnail, HOME_FEED_CARD_WIDTH);

    const avatarSrc = computed(() => {
      const { avatar, avatarNsfw } = props.item.user;
      const file = props.showNotSafeForWork ? avatarNsfw ?? avatar : avatar;
      return file ? MediaFilesController.getImageThumbnail(file.urlPart, HOME_FEED_CARD_WIDTH) : "";
    });
    const backgroundSrc = computed(() => (props.item.contentFeed ? thumbnailSrc.value : avatarSrc.value));

    const trailer = computed(() => {
      const file = getFeedTrailer(files.value);
      return file?.processingStatus === "OK" ? file : null;
    });
    const trailerSrc = computed(() => (trailer.value ? MediaFilesController.getVideoUrl(trailer.value.urlPart) : ""));
    const playRequested = computed(() => props.playTrailer || (props.playTrailerOnHover && hovered.value));

    const coParticipants = computed(() =>
      props.item.contentFeed ? getFeedCoParticipants(props.item.contentFeed) : []
    );

    const relation = computed(() => {
      if (props.item.user.subscriptionFromMe) return { name: "subscribed", icon: "msr-star-filled" };
      if (props.isFollowed) return { name: "followed", icon: "msr-push_pin-filled" };
      return null;
    });

    const userName = computed(() => props.item.user.screenName || props.item.user.username);
    const onlineStatus = computed(() => {
      const status = props.item.user.onlineStatus;
      if (status?.isOnline && !status?.invisible) return $t("Online");
      if (!status?.lastOnlineTime) return $t("Last seen recently");
      return timeAgo(status.lastOnlineTime, { addSuffix: true });
    });
    const distance = computed(() =>
      props.item.user.distance ? $t("{0} miles away", [getDistance(props.item.user)]) : null
    );
    const status = computed(() => (props.showDistance && distance.value) || onlineStatus.value);
    const description = computed(() => props.item.contentFeed?.description ?? "");
    const profileLink = computed(() => getProfileUrl(props.item.user));

    let startTimer: number | undefined;

    watch(playRequested, requested => {
      window.clearTimeout(startTimer);
      playing.value = false;
      if (!requested || !trailer.value) return;
      // a delay keeps the cards from starting videos while the pointer or the scroll just travels across the list
      startTimer = window.setTimeout(() => (playing.value = true), TRAILER_START_DELAY);
    });

    onMounted(() => emit("mounted", getElement(card.value)));

    onBeforeUnmount(() => {
      window.clearTimeout(startTimer);
      emit("unmounted", getElement(card.value));
    });

    return {
      card,
      hovered,
      playing,
      backgroundSrc,
      trailerSrc,
      coParticipants,
      relation,
      userName,
      status,
      description,
      profileLink,
    };
  },
});
